import { JOB_COPY } from "@/content/job"
import { jobSentenceParts } from "@/store/selectors"
import type { Job } from "@/types"
import styles from "@/styles/components/JobStatement.module.css"

/*
 * The locked job, carried across every round that comes after it.
 *
 * The whole point of the Job round is that the deck gets held against one
 * sentence, which only works if the sentence is still on screen while people
 * choose cards and argue about priorities. So this sits above the Cards,
 * Priorities, Deep dive and Wrap screens rather than being read once and lost.
 *
 * Renders nothing when the statement does not yet hold — an incomplete job is
 * the Job round's own business, and a half-sentence quoted at the room is worse
 * than no banner.
 */

interface JobStatementProps {
  job: Job | null
  userStoryEnabled: boolean
  /** "Problem 2 of 3", when the session has worked more than one. */
  eyebrow?: string
  /**
   * When provided, an Edit button appears on the banner, opening the job editor.
   * The caller decides when to pass it — facilitator-only, and only while the
   * session is live, since the write it leads to is refused otherwise.
   */
  onEdit?: () => void
}

export default function JobStatement({
  job,
  userStoryEnabled,
  eyebrow,
  onEdit,
}: JobStatementProps) {
  const parts = jobSentenceParts(job, userStoryEnabled)
  if (parts.length === 0) return null

  return (
    <aside className={styles.banner}>
      <span className={styles.tape} aria-hidden="true" />
      {onEdit ? (
        <button type="button" className={styles.edit} onClick={onEdit}>
          {JOB_COPY.editAction}
        </button>
      ) : null}
      <p className={styles.label}>{eyebrow ?? JOB_COPY.result}</p>
      {/* Only the facilitator's own words are bold; "As a … I want … so that …"
          stays regular so the sentence reads as a fill-in, not a headline. */}
      <p className={styles.sentence}>
        {parts.map((part, i) =>
          part.strong ? <strong key={i}>{part.text}</strong> : <span key={i}>{part.text}</span>
        )}
      </p>
    </aside>
  )
}
